import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
} from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { UserPreferencesDto } from '../remote-services/dtos/userPreferences.dto'
import { TechniqueNames } from '../remote-services/dtos/technique.dto'
import { CsvDateFormatLocalStorageService } from '../service/csv-date-format-local-storage.service'
import { DateTimeFormatService } from '../service/date-time-format.service'
import { DATE_FORMATS, DateFormats } from '../models/date-time-format.model'

export interface CsvConfigData {
  dateFormat: UserPreferencesDto['dateFormat']
  jobsiteId: string
  technique: TechniqueNames
}

export interface DateFormatDelimiter {
  format: DateFormats
  delimiter: string
}

export interface DateFormatDelimiterControls {
  dateFormat: FormControl<DateFormats>
  delimiter: FormControl<string>
}

@Component({
  selector: 'planned-progress-edit-csv-config-dialog',
  template: `
    <h3 mat-dialog-title>
      {{ 'JOBSITE_MANAGEMENT.FOLLOW_UP.CSV_DATE_FORMAT_TITLE' | translate }}
    </h3>
    <div mat-dialog-content>
      <div style="margin: 20px 0">
        <form [formGroup]="form">
          <mat-form-field>
            <input
              type="text"
              maxlength="1"
              formControlName="delimiter"
              matInput
            />
            <mat-label>{{
              'JOBSITE_MANAGEMENT.FOLLOW_UP.CSV_DATE_DELIMITER' | translate
            }}</mat-label>
            @if (form.controls?.delimiter?.hasError('required')) {
              <mat-error>{{ 'GENERAL.FIELD_REQUIRED' | translate }}</mat-error>
            }
          </mat-form-field>
          <mat-radio-group
            class="flex flex-align-space-between"
            formControlName="dateFormat"
          >
            @for (format of dateFormatOptions; track $index) {
              <mat-radio-button color="primary" [value]="format">
                {{
                  'DATE_TIME_FORMAT.DATE.' + format
                    | translate: { separator: form.controls?.delimiter?.value }
                }}
              </mat-radio-button>
            }
          </mat-radio-group>
        </form>
      </div>
    </div>

    <div mat-dialog-actions align="end">
      <button
        style="font-size: 14px"
        mat-raised-button
        type="button"
        (click)="onCancel()"
      >
        {{ 'GENERAL.CANCEL' | translate }}
      </button>
      <button
        style="font-size: 14px"
        mat-raised-button
        color="primary"
        (click)="onValidate()"
        [disabled]="!form.valid"
      >
        {{ 'GENERAL.VALIDATE' | translate }}
      </button>
    </div>
  `,
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PlannedProgressEditCsvConfigDialog implements OnInit {
  data = inject<CsvConfigData>(MAT_DIALOG_DATA)
  private dialogRef =
    inject<MatDialogRef<PlannedProgressEditCsvConfigDialog>>(MatDialogRef)
  private csvDateFormatLsService = inject(CsvDateFormatLocalStorageService)
  private formatService = inject(DateTimeFormatService)

  public form: FormGroup<DateFormatDelimiterControls>

  readonly dateFormatOptions = DATE_FORMATS

  ngOnInit(): void {
    const initialValue =
      this.csvDateFormatLsService.loadFromLocalStorageOrDefault(
        this.data.dateFormat,
        this.data.jobsiteId,
        this.data.technique,
      )
    this.form = new FormGroup({
      delimiter: new FormControl(initialValue.delimiter, [Validators.required]),
      dateFormat: new FormControl(initialValue.format, [Validators.required]),
    })
  }

  onCancel(): void {
    this.dialogRef.close(null)
  }

  onValidate(): void {
    const result = this.form.getRawValue()
    const dl = result.delimiter
    const dateFormat = this.formatService.getDateFormat(result.dateFormat, dl)
    this.csvDateFormatLsService.saveToLocalStorage(
      { delimiter: dl, format: result.dateFormat },
      this.data.jobsiteId,
      this.data.technique,
    )
    this.dialogRef.close(dateFormat)
  }
}
